import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { pathOf, type Container, type FileEntry, type SearchResult } from "@egalregal/shared";
import { PATH_SEPARATOR } from "@egalregal/shared";
import { requests } from "../../shared/api";
import { BarcodeScannerIcon } from "../../shared/components/icons";
import { PhotoCard } from "../../shared/components/PhotoCard";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { useT } from "../../shared/i18n";
import { canWriteContainer } from "../catalogs/access";
import { PlacementQuantitySnackbar } from "../items/PlacementQuantitySnackbar";
import { usePlacementQuantity } from "../items/usePlacementQuantity";
import { searchBackState } from "../shell/back";
import { useDiscardGuard } from "../shell/discardGuard";
import { MediaLightbox } from "../gallery/MediaLightbox";
import { getMirroredItems, getMirroredPlacements } from "./catalogStore";
import { embeddingService } from "./embeddingService";
import {
  DEFAULT_SEARCH_DISPLAY,
  EMPTY_SEARCH_FACETS,
  applyFacets,
  availableFacetOptions,
  type SearchDisplayState,
  type SearchFacetState,
} from "./facets";
import { PreviewPane } from "./PreviewPane";
import { ResultRow } from "./ResultRow";
import { SearchFilterPanel } from "./SearchFilterPanel";
import { applySearchState, readSearchState, searchUrl } from "./searchUrl";
import { groupSearchResults, hasGroupedResults, type SearchPhotoHit } from "./searchGroups";
import { toSearchHits } from "./searchHits";
import { SearchTextView, searchDebugEnabled } from "./SearchTextView";

interface PhotoLightboxState {
  containerId: string;
  photos: FileEntry[];
  index: number;
}

/**
 * Search results: each row carries a fit thumbnail, name, the full location
 * path, a two-line description and type/owner/tag chips (ADR-0001 amendment).
 * Clicking a row expands level 2 in place (facts, preview strip, contents); the
 * trailing right arrow opens the hit directly; level 2's Preview opens the
 * overlay preview pane. Container hits open the detail page; item hits open the
 * container containing the item, unless the item has no placement yet — then
 * the item's own detail view opens.
 *
 * The filter table replaces the old scope chips (search-result-disclosure 04):
 * scope is an exclusive row writing the URL-owned `?scope=`, owner/tag/type are
 * contextual facets (OR inside a property, AND across; item owner/tag inherited
 * from the placement container, ADR-0022 amendment), and the display rows are
 * show/hide values for previews and descriptions. Filters narrow the ranked
 * result set only; the query itself stays untouched.
 *
 * The query, mode and scope come from the URL (shell-redesign 07/09, spec §5):
 * the view never reads a store query, every control writes with replace, and
 * leaving the view drops the params. The engine (keyword/semantic) is an
 * exclusive row inside the filter table. An empty query shows recently opened
 * containers instead of an empty state.
 *
 * Photo results split their target: the image opens the owning container's
 * photos in the gallery `MediaLightbox` over the results, while the caption
 * opens that container's detail with the search target as back state.
 *
 * Model loading (ticket 06) mirrors the gallery's appearance mode: while a
 * free-text search awaits the EmbeddingGemma model, a determinate bar tracks
 * real byte progress on a cold miss and an indeterminate indicator covers the
 * warm store read + session init (never faked). Pure-discrete lookups never
 * touch the model, so they resolve with no bar. The bar only reflects loading
 * — the implicit keyword fallback stays invisible per ADR-0022 B4.
 *
 * Dev builds additionally mount the searchable-text inspector (ADR-0033):
 * with `?debug=search` the results markup is swapped for it, gated by
 * `searchDebugEnabled` so production bundles drop the branch entirely.
 */
export function SearchView() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const results = useAppStore((s) => s.searchResults);
  const searchLoading = useAppStore((s) => s.searchLoading);
  const searchProgress = useAppStore((s) => s.searchProgress);
  const pool = useAppStore(readableContainers);
  const recentContainerIds = useAppStore((s) => s.recentContainerIds);
  const search = useAppStore((s) => s.search);
  const [lightbox, setLightbox] = useState<PhotoLightboxState | null>(null);
  const [searchParams] = useSearchParams();
  const state = readSearchState(searchParams);
  const { q, mode, scope, facets } = state;
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const refreshResults = useCallback(() => {
    void search(q, mode);
  }, [q, mode, search]);
  const { notice, dismiss, quantityOf, change, undo } = usePlacementQuantity(refreshResults);
  // Every result opened from this view carries the exact results target, so the
  // detail's back link (and history) return here (spec §5–6).
  const searchOrigin = searchBackState(location.search);
  // The latest query without making the index-build effect re-run per
  // keystroke; the build reruns the search once when it finishes.
  const queryRef = useRef(q);
  queryRef.current = q;
  const openContainer = useOpenContainer();

  // URL writes always replace: typing, mode, scope and filters never stack
  // history entries (spec §5). Filters are URL-owned (ticket 09).
  const writeSearch = (patch: Partial<typeof state>) => {
    const params = applySearchState(new URLSearchParams(location.search), { ...state, ...patch });
    navigate(searchUrl(params), { replace: true });
  };
  const setFacets = (next: SearchFacetState) => writeSearch({ facets: next });

  // The URL is the only query source: typing replaces the entry, so a debounce
  // keeps one search per pause instead of one per keystroke. The pool is a
  // dependency so a reload/deep link re-runs once the catalog has arrived.
  useEffect(() => {
    if (!q.trim()) {
      void search("");
      return;
    }
    const timer = setTimeout(() => void search(q, mode), 200);
    return () => clearTimeout(timer);
  }, [q, mode, scope, pool, search]);

  // Semantic is opt-in: entering it builds the index (with embeddings) once,
  // then reruns the search so ranking sees the freshly built vectors. Keyword
  // mode never wakes the model (ADR-0022 B5); an empty pool is still
  // loading — syncing then would prune the persisted index, so wait for it.
  useEffect(() => {
    if (mode !== "semantic" || pool.length === 0) return;
    let cancelled = false;
    void (async () => {
      const app = useAppStore.getState();
      await app.syncSearchIndex(readableContainers(app), { embed: true });
      if (cancelled) return;
      const query = queryRef.current.trim();
      // The debounced search may already have ranked before the build
      // finished; run once more now that the vectors exist.
      if (query) await useAppStore.getState().search(query, "semantic");
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, pool]);

  // Mirror the gallery's mount/unmount progress pattern: the singleton holds
  // a single listener, so register on mount and clear on unmount — no leaked
  // listeners, no writes after unmount.
  useEffect(() => {
    embeddingService.onProgress((fraction) => {
      useAppStore.setState({ searchProgress: fraction });
    });
    return () => {
      embeddingService.onProgress(undefined);
    };
  }, []);

  /**
   * The scan shortcut is a chrome destination like the app-bar button: the
   * scan view is the single place a decoded code resolves (scanner-surface 04).
   * It goes through the guard so an open draft still gets its prompt.
   */
  const guard = useDiscardGuard();
  const openScanner = () => guard.request(() => navigate("/scan"));

  const handleResultClick = (result: SearchResult) => {
    const from = searchOrigin;
    if (result.type === "container") {
      void openContainer(result.container.id, from);
    } else if (result.type === "item") {
      // An item with no placement (containerId "") has no container to open —
      // its own detail view is the only home it has.
      if (!result.containerId) {
        navigate(`/items/${result.item.id}`, { state: from });
        return;
      }
      void openContainer(result.containerId, from);
    }
  };

  /** Open the owning container's photos at the clicked result (spec §5). */
  const openPhoto = (hit: SearchPhotoHit) => {
    const photos = hit.container.files.filter((file) => file.kind === "photo");
    const index = photos.findIndex((file) => file.id === hit.file.id);
    setLightbox({ containerId: hit.container.id, photos, index: index < 0 ? 0 : index });
  };

  const [display, setDisplay] = useState<SearchDisplayState>(DEFAULT_SEARCH_DISPLAY);
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  // Rows are derived from the ranked results (ADR-0001 amendment): media from
  // the preview endpoints, placement-derived owner/tags for the facets, and a
  // container's contents from the item mirror.
  const hits = useMemo(
    () => toSearchHits(results, pool, getMirroredItems(), getMirroredPlacements()),
    [results, pool],
  );
  const facetHits = useMemo(() => applyFacets(hits, facets), [hits, facets]);
  const options = useMemo(() => availableFacetOptions(hits, facets), [hits, facets]);
  const filteredResults = useMemo(() => facetHits.map((hit) => hit.result), [facetHits]);
  const groups = groupSearchResults(filteredResults, scope);
  const showContainers = scope === "all" || scope === "containers";
  const showItems = scope === "all" || scope === "items";
  const containerHits = showContainers
    ? facetHits.filter((hit) => hit.result.type === "container")
    : [];
  const itemHits = showItems ? facetHits.filter((hit) => hit.result.type === "item") : [];
  const activeFacetCount = facets.owners.length + facets.tags.length + facets.types.length;
  const previewHit = previewKey
    ? (facetHits.find((hit) => hit.key === previewKey) ?? null)
    : null;

  const recentContainers = recentContainerIds
    .map((id) => pool.find((container) => container.id === id))
    .filter((container): container is Container => container !== undefined);

  // Dev-gated searchable-text inspector (ADR-0033): replaces the results
  // markup only on /search?debug=search in a dev build. import.meta.env.DEV is
  // statically replaced, so production bundles dead-code-eliminate this branch
  // and the inspector with it — and nothing links to it, ever.
  if (searchDebugEnabled(searchParams, import.meta.env.DEV)) {
    return <SearchTextView />;
  }

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography component="h1" variant="h6" tabIndex={-1} sx={{ flexGrow: 1 }}>
          {t("searchResults")}
        </Typography>
        <IconButton aria-label={t("scan")} onClick={openScanner} data-testid="search-scan">
          <BarcodeScannerIcon />
        </IconButton>
      </Stack>

      {q.trim() !== "" && (
        <SearchFilterPanel
          scope={scope}
          onScopeChange={(next) => writeSearch({ scope: next })}
          mode={mode}
          onModeChange={(next) => writeSearch({ mode: next })}
          facets={facets}
          onFacets={setFacets}
          display={display}
          onDisplay={setDisplay}
          options={options}
        />
      )}

      {searchLoading && (
        <Box sx={{ mb: 1 }} data-testid="search-loading">
          <LinearProgress
            variant={searchProgress !== null ? "determinate" : "indeterminate"}
            value={searchProgress !== null ? Math.round(searchProgress * 100) : undefined}
            aria-label={t("appearanceDownloading")}
          />
          <Typography variant="caption" color="text.secondary">
            {searchProgress !== null ? t("appearanceDownloading") : t("appearanceLoading")}
          </Typography>
        </Box>
      )}

      {q.trim() === "" ? (
        <RecentResults
          containers={recentContainers}
          pool={pool}
          onOpen={(id) => void openContainer(id, searchOrigin)}
        />
      ) : !hasGroupedResults(groups) && !searchLoading ? (
        // Never show "no matches" for a load in flight: a ~200MB first-visit
        // model load must not look identical to "no results".
        <>
          <Typography variant="body1" color="text.secondary">
            {t("noMatches")}
          </Typography>
          {activeFacetCount > 0 && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => setFacets(EMPTY_SEARCH_FACETS)}>
              {t("searchClearFilters")}
            </Button>
          )}
        </>
      ) : (
        <Stack spacing={1}>
          {containerHits.length > 0 && (
            <ResultGroup label={t("scopeContainers")}>
              {containerHits.map((hit) => (
                <ResultRow
                  key={hit.key}
                  hit={hit}
                  display={display}
                  onOpen={() => handleResultClick(hit.result)}
                  onPreview={() => setPreviewKey(hit.key)}
                />
              ))}
            </ResultGroup>
          )}
          {itemHits.length > 0 && (
            <ResultGroup label={t("scopeItems")}>
              {itemHits.map((hit) => {
                const result = hit.result;
                const item = result.type === "item" ? result.item : undefined;
                const containerId = result.type === "item" ? result.containerId : "";
                const quantity =
                  item && containerId
                    ? quantityOf(containerId, item.id, hit.quantity)
                    : hit.quantity;
                return (
                  <ResultRow
                    key={hit.key}
                    hit={hit}
                    display={display}
                    onOpen={() => handleResultClick(hit.result)}
                    onPreview={() => setPreviewKey(hit.key)}
                    onOpenPlacement={(placementId) =>
                      void openContainer(placementId, searchOrigin)
                    }
                    onOpenContainer={
                      containerId
                        ? () => void openContainer(containerId, searchOrigin)
                        : undefined
                    }
                    quantity={quantity}
                    canWrite={
                      containerId
                        ? canWriteContainer(
                            pool,
                            entries,
                            sharedWithMe,
                            containerId,
                            selectedEntryId,
                          )
                        : true
                    }
                    onQuantityChange={
                      item && containerId && quantity !== undefined
                        ? (next) => {
                            void change({ item, containerId, next });
                          }
                        : undefined
                    }
                  />
                );
              })}
            </ResultGroup>
          )}
          {groups.photos.length > 0 && (
            <ResultGroup label={t("scopePhotos")}>
              <PhotoResults
                photos={groups.photos}
                onOpenImage={openPhoto}
                onOpenContainer={(id) =>
                  void openContainer(id, searchOrigin)
                }
              />
            </ResultGroup>
          )}
        </Stack>
      )}

      <PlacementQuantitySnackbar
        notice={notice}
        onClose={dismiss}
        onUndo={() => void undo()}
      />

      <PreviewPane
        hit={previewHit}
        onOpen={() => {
          if (previewHit) handleResultClick(previewHit.result);
        }}
        onClose={() => setPreviewKey(null)}
      />

      {lightbox && (
        <MediaLightbox
          open
          containerId={lightbox.containerId}
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(index) => setLightbox((current) => (current ? { ...current, index } : current))}
        />
      )}
    </>
  );
}

/** One scope group: an h2 heading above its rows. */
function ResultGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography component="h2" variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** Recently opened containers shown for an empty query (spec §5). */
function RecentResults({
  containers,
  pool,
  onOpen,
}: {
  containers: Container[];
  pool: Container[];
  onOpen: (id: string) => void;
}) {
  const t = useT();
  if (containers.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t("homeHint")}
      </Typography>
    );
  }
  return (
    <ResultGroup label={t("searchRecent")}>
      <List disablePadding>
        {containers.map((container) => (
          <ListItem key={container.id} disablePadding>
            <ListItemButton onClick={() => onOpen(container.id)}>
              <ListItemText primary={container.name} secondary={displayPath(pool, container.id)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </ResultGroup>
  );
}

/** Photo results: the shared card; image → lightbox, caption → container. */
function PhotoResults({
  photos,
  onOpenImage,
  onOpenContainer,
}: {
  photos: SearchPhotoHit[];
  onOpenImage: (hit: SearchPhotoHit) => void;
  onOpenContainer: (containerId: string) => void;
}) {
  const t = useT();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: 1,
        px: 1,
        py: 0.5,
      }}
    >
      {photos.map((hit) => {
        const identity = { uuid: hit.container.id, name: hit.container.name };
        const panelActions = [
          {
            key: "container",
            label: t("goToContainer"),
            onClick: () => onOpenContainer(hit.container.id),
          },
        ];
        const related = hit.container.files
          .filter((file) => file.kind === "photo" && file.id !== hit.file.id)
          .slice(0, 32)
          .map((file) => ({
            key: file.id,
            image: requests.media.previewUrl(hit.container.id, file.id),
            alt: file.name,
            title: file.name,
            secondary: hit.path,
            identity,
            actions: panelActions,
            onOpen: () =>
              onOpenImage({ container: hit.container, file, path: hit.path }),
          }));
        return (
        <PhotoCard
          key={hit.file.id}
          image={requests.media.previewUrl(hit.container.id, hit.file.id)}
          imageAlt={hit.file.name}
          title={hit.file.name}
          secondary={hit.path}
          identity={identity}
          onOpen={() => onOpenImage(hit)}
          onCaptionClick={() => onOpenContainer(hit.container.id)}
          related={related}
          actions={panelActions}
          cardTestId="search-photo"
          mediaTestId="search-photo-image"
          captionTestId="search-photo-caption"
        />
        );
      })}
    </Box>
  );
}

/** Breadcrumb-style location path for a recent container. */
function displayPath(containers: Container[], id: string): string {
  return pathOf(containers, id)
    .map((container) => container.name)
    .join(PATH_SEPARATOR);
}
